import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { MESSAGES } from "@/constants/messages";

type DatePickerProps = {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  className?: string;
  fromYear?: number;
  toYear?: number;
  onOpenChange?: (open: boolean) => void;
  autoFocus?: boolean;
  error?: string | null;
  onValidationError?: (error: string | null) => void;
  preserveInputOnEmpty?: boolean;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

// Helper function for dynamic day range validation
const getMaxDays = (month: string, year: string): number => {
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (isNaN(m)) return 31;

  if (m === 2) {
    // If year is not entered yet, allow 29 days (be permissive)
    if (isNaN(y)) return 29;

    const isLeapYear = (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
    return isLeapYear ? 29 : 28;
  }

  if ([4, 6, 9, 11].includes(m)) {
    return 30;
  }

  return 31;
};

// Generic real-time validation function with min/max bounds
const isValidPart = (
  value: string,
  max: number,
  maxLength: number,
  partType: "MM" | "DD" | "YYYY"
): boolean => { // NOSONAR
  if (value === "") return true;
  if (!/^\d+$/.test(value)) return false;
  if (value.length > maxLength) return false;

  const numValue = parseInt(value, 10);

  // First digit validation
  if (value.length === 1) {
    if (partType === "MM") return numValue <= 9;
    if (partType === "DD") return numValue <= 9;
    if (partType === "YYYY") return numValue === 1 || numValue === 2;
    return true;
  }

  // Full length validation for 2-digit parts (MM, DD)
  if (value.length === 2) {
    if (partType === "MM") return numValue >= 1 && numValue <= 12;
    if (partType === "DD") return numValue >= 1 && numValue <= max;
    if (partType === "YYYY") return numValue >= 19 && numValue <= 20;
  }

  // Full length validation for YYYY
  if (value.length === 4 && partType === "YYYY") {
    return numValue >= 1901 && numValue <= max;
  }

  return true;
};

// Separate input component for MM/DD/YYYY parts
type DatePartInputProps = {
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  maxLength: number;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
};

const DatePartInput = React.forwardRef<HTMLInputElement, DatePartInputProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      onFocus,
      onBlur,
      maxLength,
      placeholder,
      className,
      autoFocus,
    },
    ref
  ) => {

  // Dynamic width logic for each field
  let widthClass = "";
  if (placeholder === "MM") {
    // MM field: 14px with value, 24px for placeholder
    widthClass = "w-[24px]";
  } else if (placeholder === "DD") {
    // DD field: min 14px max 18px with value, 20px for placeholder
    widthClass = "w-[24px]";
  } else if (placeholder === "YYYY") {
    // YYYY field
    widthClass = "flex-1"; // Takes remaining space
  } else {
    // Fallback
    widthClass = maxLength === 2 ? "w-6" : "flex-1";
  }

  return (
    <input
      ref={ref}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      maxLength={maxLength}
      placeholder={placeholder}
      className={cn(
        "bg-transparent border-0 focus:ring-0 focus:ring-offset-0 p-0 text-left outline-[none]",
        "text-[14px] leading-[22px] font-normal font-inter",
        "placeholder:text-grey-600",
        "transition-all duration-200 ease-in-out",
        widthClass,
        className
      )}
      autoFocus={autoFocus}
    />
  );
  }
);

DatePartInput.displayName = "DatePartInput";

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  className,
  fromYear = 1901,
  toYear = new Date().getFullYear(),
  onOpenChange,
  autoFocus = false,
  error,
  onValidationError,
  preserveInputOnEmpty = false,
  onInputFocus,
  onInputBlur,
  onInputKeyDown,
}) => {
  const [open, setOpen] = React.useState(false);
  const [monthValue, setMonthValue] = React.useState("");
  const [dayValue, setDayValue] = React.useState("");
  const [yearValue, setYearValue] = React.useState("");
  const containerRef = React.useRef<HTMLDivElement>(null);
  const monthRef = React.useRef<HTMLInputElement>(null);
  const dayRef = React.useRef<HTMLInputElement>(null);
  const yearRef = React.useRef<HTMLInputElement>(null);
  const [isTyping, setIsTyping] = React.useState(false);
  const [popoverWidth, setPopoverWidth] = React.useState<number | undefined>(
    undefined
  );
  const [displayMonth, setDisplayMonth] = React.useState<Date>(
    () => value || new Date()
  );
  const prevValueRef = React.useRef<Date | undefined>(undefined);

  // Check individual fields for separator colors
  const hasMonthValue = !!monthValue;
  const hasDayValue = !!dayValue;

  // Update input values when value prop changes
  React.useEffect(() => {
    const prev = prevValueRef.current;

    // If value hasn't changed since last sync, do nothing (preserve user's input)
    const valueChanged =
      (value && !prev) ||
      (!value && prev) ||
      (value && prev && value.getTime() !== prev.getTime());

    if (!valueChanged) return;

    if (value) {
      // Only sync inputs from prop value if not currently typing (preserve user input)
      if (!isTyping) {
        const formatted = format(value, "MM/dd/yyyy");
        const [month, day, year] = formatted.split('/');
        setMonthValue(month);
        setDayValue(day);
        setYearValue(year);
        // Update display month to show the selected date's month
        setDisplayMonth(value);
      }
    } else {
      // Always clear inputs when value becomes undefined (e.g., on redirection)
      // regardless of typing state - this ensures clean state on navigation
      if (!preserveInputOnEmpty) {
        setMonthValue("");
        setDayValue("");
        setYearValue("");
      }
      // Reset to current month when no date is selected
      setDisplayMonth(new Date());
    }

    prevValueRef.current = value;
  }, [value, isTyping, preserveInputOnEmpty]);

  // Watch for complete date formation and trigger validation
  React.useEffect(() => {
    if (monthValue && dayValue && yearValue) {
      // Check if we have a potentially complete date
      const paddedMonth = monthValue.padStart(2, '0');
      const paddedDay = dayValue.padStart(2, '0');
      const finalYear = yearValue.length >= 4 ? yearValue.slice(0, 4) : yearValue;

      // If all parts look complete, validate
      if (paddedMonth.length === 2 && paddedDay.length === 2 && finalYear.length === 4) {
        validateAndSubmitCompleteDate();
      }
    }
  }, [monthValue, dayValue, yearValue]);

  // Auto focus the month input when autoFocus is true
  React.useEffect(() => {
    if (autoFocus && monthRef.current) {
      monthRef.current.focus();
    }
  }, [autoFocus]);

  const handleOpenChange = (next: boolean) => {
    if (next && containerRef.current) {
      // Measure the container width when opening the popover
      setPopoverWidth(containerRef.current.offsetWidth);
      // When opening, set display month to the selected date or current date
      if (value) {
        setDisplayMonth(value);
      } else {
        setDisplayMonth(new Date());
      }
    }
    setOpen(next);
    onOpenChange?.(next);
  };



  // Helper function to validate complete date and call onChange
  const validateAndSubmitCompleteDate = () => { // NOSONAR
    // Check if we have all required parts
    if (monthValue && dayValue && yearValue) {
      // Ensure proper lengths (pad if necessary)
      const paddedMonth = monthValue.padStart(2, '0');
      const paddedDay = dayValue.padStart(2, '0');
      const finalYear = yearValue.length >= 4 ? yearValue.slice(0, 4) : yearValue;

      // Only validate if we have complete parts
      if (paddedMonth.length === 2 && paddedDay.length === 2 && finalYear.length === 4) {
        const combined = `${paddedMonth}/${paddedDay}/${finalYear}`;
        const parsedDate = parse(combined, "MM/dd/yyyy", new Date());

        if (isValid(parsedDate)) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (parsedDate > today) {
            onValidationError?.(MESSAGES.futureDate);
          } else {
            onValidationError?.(null);
            onChange(parsedDate);
          }
        } else {
          onValidationError?.(MESSAGES.dobInvalid);
        }
      }
    }
  };

  // Handle input changes for each part with auto-navigation
  const handleMonthChange = (val: string) => {
    // Strip non-numeric and length check first (standard behavior)
    const numericVal = val.replace(/\D/g, '').slice(0, 2);

    // Enforce strict real-time validation (1-12)
    if (!isValidPart(numericVal, 12, 2, "MM")) {
      onValidationError?.("Please enter correct MM.");
      return;
    }

    onValidationError?.(null);

    // Auto-normalize single digits 2-9 to two digits (e.g., '2' -> '02')
    let finalVal = numericVal;
    if (numericVal.length === 1) {
      const numValue = parseInt(numericVal, 10);
      if (numValue >= 2 && numValue <= 9) {
        finalVal = `0${numericVal}`;
      }
    }

    setMonthValue(finalVal);
    setIsTyping(true);
    onInputFocus?.();

    if (finalVal.length === 2 && dayRef.current) {
      setTimeout(() => {
        dayRef.current?.focus();
        if (dayRef.current) {
          dayRef.current.setSelectionRange(0, 0);
        }
      }, 0);
    }
  };

  const handleDayChange = (val: string) => {
    const numericVal = val.replace(/\D/g, '').slice(0, 2);

    // Enforce strict real-time validation (1-maxDays)
    const maxDays = getMaxDays(monthValue, yearValue);
    if (!isValidPart(numericVal, maxDays, 2, "DD")) {
      onValidationError?.("Please enter correct DD.");
      return;
    }

    onValidationError?.(null);

    // Auto-normalize single digits 4-9 to two digits (e.g., '4' -> '04')
    // Digits 1, 2, 3 are preserved to allow typing 10-31
    let finalVal = numericVal;
    if (numericVal.length === 1) {
      const numValue = parseInt(numericVal, 10);
      if (numValue >= 4 && numValue <= 9) {
        finalVal = `0${numericVal}`;
      }
    }

    setDayValue(finalVal);
    setIsTyping(true);
    onInputFocus?.();

    if (finalVal.length === 2 && yearRef.current) {
      setTimeout(() => {
        yearRef.current?.focus();
        if (yearRef.current) {
          yearRef.current.setSelectionRange(0, 0);
        }
      }, 0);
    }
  };

  const handleYearChange = (val: string) => {
    const numericVal = val.replace(/\D/g, '').slice(0, 4);

    // Enforce strict real-time validation (up to toYear)
    if (!isValidPart(numericVal, toYear, 4, "YYYY")) {
      onValidationError?.("Please enter correct YYYY.");
      return;
    }

    onValidationError?.(null);
    setYearValue(numericVal);
    setIsTyping(true);
    onInputFocus?.();
  };

  // Handle key navigation between inputs
  const handleMonthKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      // Pad single digit month before navigating
      if (monthValue && monthValue.length === 1) {
        const padded = `0${monthValue}`;
        if (padded === "00") {
          onValidationError?.("Please enter correct MM.");
        } else {
          setMonthValue(padded);
        }
      } else if (monthValue === "00") {
        onValidationError?.("Please enter correct MM.");
      }
      dayRef.current?.focus();
    }
    if (e.key === 'ArrowRight' && (e.target as HTMLInputElement).selectionStart === monthValue.length && monthValue.length > 0 && !e.shiftKey) {
      e.preventDefault();
      dayRef.current?.focus();
    }
    onInputKeyDown?.(e);
  };

  const handleDayKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => { // NOSONAR
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      // Pad single digit day before navigating
      if (dayValue && dayValue.length === 1) {
        const padded = `0${dayValue}`;
        if (padded === "00") {
          onValidationError?.("Please enter correct DD.");
        } else {
          setDayValue(padded);
        }
      } else if (dayValue === "00") {
        onValidationError?.("Please enter correct DD.");
      }
      yearRef.current?.focus();
    }
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      // Pad single digit day before navigating back
      if (dayValue && dayValue.length === 1) {
        const padded = `0${dayValue}`;
        if (padded === "00") {
          onValidationError?.("Please enter correct DD.");
        } else {
          setDayValue(padded);
        }
      } else if (dayValue === "00") {
        onValidationError?.("Please enter correct DD.");
      }
      monthRef.current?.focus();
    }
    if (e.key === 'ArrowLeft' && (e.target as HTMLInputElement).selectionStart === 0 && !e.shiftKey) {
      e.preventDefault();
      monthRef.current?.focus();
    }
    if (e.key === 'ArrowRight' && (e.target as HTMLInputElement).selectionStart === dayValue.length && dayValue.length > 0 && !e.shiftKey) {
      e.preventDefault();
      yearRef.current?.focus();
    }
    // Auto backspace navigation: if cursor at start and field is empty, move to month
    if (e.key === 'Backspace' && (e.target as HTMLInputElement).selectionStart === 0 && (e.target as HTMLInputElement).value === '' && !e.shiftKey) {
      e.preventDefault();
      monthRef.current?.focus();
    }
    onInputKeyDown?.(e);
  };

  const handleYearKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      dayRef.current?.focus();
    }
    if (e.key === 'ArrowLeft' && (e.target as HTMLInputElement).selectionStart === 0 && !e.shiftKey) {
      e.preventDefault();
      dayRef.current?.focus();
    }
    // Auto backspace navigation: if cursor at start and field is empty, move to day
    if (e.key === 'Backspace' && (e.target as HTMLInputElement).selectionStart === 0 && (e.target as HTMLInputElement).value === '' && !e.shiftKey) {
      e.preventDefault();
      dayRef.current?.focus();
    }
    onInputKeyDown?.(e);
  };

  // Handle focus/blur for each input
  const handleMonthFocus = () => {
    setIsTyping(true);
    onInputFocus?.();
    // Position cursor at the end immediately for smooth continuation
    if (monthRef.current) {
      const length = monthValue.length;
      monthRef.current.setSelectionRange(length, length);
    }
  };

  const handleDayFocus = () => {
    setIsTyping(true);
    onInputFocus?.();
    // Position cursor at the end immediately for smooth continuation
    if (dayRef.current) {
      const length = dayValue.length;
      dayRef.current.setSelectionRange(length, length);
    }
  };

  const handleYearFocus = () => {
    setIsTyping(true);
    onInputFocus?.();
    // Position cursor at the end immediately for smooth continuation
    if (yearRef.current) {
      const length = yearValue.length;
      yearRef.current.setSelectionRange(length, length);
    }
  };

  const handleMonthBlur = () => {
    // Auto-pad single digit months with leading zero
    if (monthValue && monthValue.length === 1) {
      const padded = `0${monthValue}`;
      if (padded === "00") {
        onValidationError?.("Please enter correct MM.");
      } else {
        setMonthValue(padded);
      }
    } else if (monthValue === "00") {
      onValidationError?.("Please enter correct MM.");
    }
  };

  const handleDayBlur = () => {
    // Auto-pad single digit days with leading zero
    if (dayValue && dayValue.length === 1) {
      const padded = `0${dayValue}`;
      if (padded === "00") {
        onValidationError?.("Please enter correct DD.");
      } else {
        setDayValue(padded);
      }
    } else if (dayValue === "00") {
      onValidationError?.("Please enter correct DD.");
    }
  };

  const handleYearBlur = () => {
    // No validation on individual field blur - only on complete date
  };

  // Combined blur handler
  const handleCombinedBlur = () => {
    setIsTyping(false);

    // If all fields are empty, clear the date
    if (!monthValue && !dayValue && !yearValue) {
      onChange(undefined);
      onValidationError?.(null);
      onInputBlur?.();
      return;
    }

    setOpen(false);
    onInputBlur?.();
  };

  const handleCalendarIconClick = () => {
    setOpen(!open);
  };

  // Smart paste handler for MM/DD/YYYY format
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').trim();

    // Check if pasted text matches MM/DD/YYYY format
    const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = pastedText.match(datePattern);

    if (match) {
      const [, month, day, year] = match;

      // Pad month and day to 2 digits
      const paddedMonth = month.padStart(2, '0');
      const paddedDay = day.padStart(2, '0');

      // Set the values
      setMonthValue(paddedMonth);
      setDayValue(paddedDay);
      setYearValue(year);
      setIsTyping(true);

      // Trigger validation
      setTimeout(() => validateAndSubmitCompleteDate(), 0);
    }
  };

  return (
    <div className='relative w-full'>
      <div
        ref={containerRef}
        className={cn(
          // Match input typography (size, weight, family, color)
          "flex h-12 w-full items-center border px-4 text-left gap-0.5",
          "text-[14px] leading-[22px] font-normal font-inter",
          "bg-grey-100 text-grey-800 shadow-sm rounded-md",
          "dark:bg-blue-800 dark:text-grey-100 dark:border-grey-700",
          "focus-within:outline-none focus-within:ring-0 focus-within:ring-offset-0",
          error ? "border-red-500" : "border-grey-400/70",
          className
        )}
        onFocus={() => {
          setIsTyping(true);
          // If no field is focused, focus on month
          if (!monthValue && !dayValue && !yearValue && monthRef.current) {
            monthRef.current.focus();
          }
        }}
        onBlur={handleCombinedBlur}
        onPaste={handlePaste}
      >
        <DatePartInput
          ref={monthRef}
          value={monthValue}
          onChange={handleMonthChange}
          onKeyDown={handleMonthKeyDown}
          onFocus={handleMonthFocus}
          onBlur={handleMonthBlur}
          maxLength={2}
          placeholder="MM"
          autoFocus={autoFocus}
        />
        <span className={`mx-0.5 ${hasMonthValue ? 'text-grey-800 dark:text-grey-100' : 'text-grey-500'}`}>/</span>
        <DatePartInput
          ref={dayRef}
          value={dayValue}
          onChange={handleDayChange}
          onKeyDown={handleDayKeyDown}
          onFocus={handleDayFocus}
          onBlur={handleDayBlur}
          maxLength={2}
          placeholder="DD"
        />
        <span className={`mx-0.5 ${hasDayValue ? 'text-grey-800 dark:text-grey-100' : 'text-grey-500'}`}>/</span>
        <DatePartInput
          ref={yearRef}
          value={yearValue}
          onChange={handleYearChange}
          onKeyDown={handleYearKeyDown}
          onFocus={handleYearFocus}
          onBlur={handleYearBlur}
          maxLength={4}
          className="w-[100px]"
          placeholder="YYYY"
        />
      </div>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type='button'
            onClick={handleCalendarIconClick}
            data-calendar-icon-button
            className='absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md transition-colors bg-transparent hover:bg-grey-300 dark:hover:bg-blue-700 border-0 z-10'>
            <CalendarIcon className='h-4 w-4 text-grey-600 dark:text-grey-300' />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className='p-0 border border-border bg-popover shadow-[0px_12px_30px_rgba(15,23,42,0.35)]'
          align='end'
          side='bottom'
          sideOffset={12}
          style={
            popoverWidth
              ? { width: `${popoverWidth}px`, minWidth: "280px" }
              : { width: "auto", minWidth: "280px" }
          }>
          <Calendar
            mode='single'
            selected={value}
            month={displayMonth}
            onMonthChange={setDisplayMonth}
            onSelect={(selectedDate) => {
              setIsTyping(false);

              if (selectedDate) {
                setOpen(false);
                const formatted = format(selectedDate, "MM/dd/yyyy");
                const [month, day, year] = formatted.split('/');
                setMonthValue(month);
                setDayValue(day);
                setYearValue(year);
                setDisplayMonth(selectedDate);

                // Validate the selected date is not in the future
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                if (selectedDate > today) {
                  // Show future date error but still update the value
                  if (onValidationError) {
                    onValidationError(MESSAGES.futureDate);
                  }
                  onChange(selectedDate);
                  return;
                }

                // Clear any errors if date is valid
                if (onValidationError) {
                  onValidationError(null);
                }

                onChange(selectedDate);
              } else {
                onChange(selectedDate);
              }
            }}
            captionLayout='dropdown'
            startMonth={new Date(fromYear, 0)}
            endMonth={new Date(toYear, 11)}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export { DatePicker };
